/**
 * Os quatro números da estante no Perfil (próprio e público).
 *
 * ⚠️ **Os quatro NÃO somam o total da estante, e isso é o desenho.** Três deles
 * recortam o ciclo de leitura (`status`), que é exclusivo; o quarto recorta a
 * POSSE (`ownership`), que é um eixo independente — um livro que você está
 * lendo e também tem conta nos dois. Quem espera `soma === allBooks.length`
 * está lendo o modelo antigo, de quando posse era um valor de `status`.
 *
 * Livros em `status: "owned"` ("tenho, sem plano de leitura") aparecem SÓ no
 * recorte de posse: não há intenção de leitura declarada pra contar em lugar
 * nenhum.
 *
 * Uma passada só sobre a lista, guardando também as duas primeiras capas de
 * cada recorte para as mini-capas dos tiles.
 */

import type { BookStatus, UserBook } from "../../../services/firebase/booksService";
import { isBookOwned } from "./bookOwnership";

export interface ShelfTileStats {
  shelfCounts: Record<BookStatus, number>;
  shelfCovers: Record<BookStatus, string[]>;
}

/** Quantas capas cada tile mostra. */
const COVERS_PER_TILE = 2;

export function computeShelfTileStats(books: UserBook[]): ShelfTileStats {
  const shelfCounts: Record<BookStatus, number> = {
    want_to_read: 0,
    reading: 0,
    read: 0,
    owned: 0,
  };
  const shelfCovers: Record<BookStatus, string[]> = {
    want_to_read: [],
    reading: [],
    read: [],
    owned: [],
  };

  const push = (key: BookStatus, book: UserBook) => {
    shelfCounts[key] += 1;
    if (shelfCovers[key].length < COVERS_PER_TILE && book.coverUrl) {
      shelfCovers[key].push(book.coverUrl);
    }
  };

  for (const book of books) {
    // Eixo do ciclo de leitura. `owned` fica de fora aqui de propósito — ele é
    // o recorte de posse, resolvido logo abaixo, e contá-lo nos dois lugares
    // dobraria o livro "só tenho" dentro do próprio tile.
    if (
      book.status === "want_to_read" ||
      book.status === "reading" ||
      book.status === "read"
    ) {
      push(book.status, book);
    }

    // Eixo de posse — independente do acima, e tolerante ao doc legado sem o
    // campo (`isBookOwned` deriva de `status: "owned"`).
    if (isBookOwned(book)) push("owned", book);
  }

  return { shelfCounts, shelfCovers };
}
